import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type { EditorJsData } from '@/types'

export type EditorJsFieldProps = {
  value: EditorJsData
  onChange: (next: EditorJsData) => void
  readOnly?: boolean
  /**
   * Фон области редактора в режиме только чтения: `disabled` — серый (по умолчанию),
   * `paper` — как у редактируемого поля (белый в светлой теме).
   */
  readOnlySurface?: 'disabled' | 'paper'
  label?: string
  rows?: number
  hideLabel?: boolean
  containerSx?: SxProps<Theme>
  holderSx?: SxProps<Theme>
  /**
   * Задержка перед созданием Editor.js (мс). Для модалок и скрытых контейнеров
   * иногда нужно 50–100 мс после появления в DOM.
   */
  initDelayMs?: number
  /** Фокус внутри редактора (capture), для блокировок по сокетам */
  onFocus?: () => void
  /** Потеря фокуса с области редактора */
  onBlur?: () => void
  /**
   * Загрузчик изображений: включает image-блок (@editorjs/image).
   * Не задан — набор инструментов прежний (заметки урока, ДЗ не меняются).
   */
  imageUploader?: (file: File) => Promise<{ url: string; path?: string }>
}

function normalizeEditorData(value: EditorJsData): EditorJsData {
  // If API provided an explicit blocks array (even empty), don't "help" Editor.js by injecting blocks.
  if (Array.isArray(value?.blocks)) {
    if (value.blocks.length > 0) return value
    // If blocks is explicitly empty, we still pass a single empty paragraph.
    // This makes initial rendering deterministic and prevents "double empty paragraph" states.
    return {
      time: value?.time,
      version: value?.version ?? '2.0.0',
      blocks: [
        {
          type: 'paragraph',
          data: { text: '' },
        },
      ],
    }
  }

  // For non-standard/undefined payloads, inject a minimal paragraph so the editor always has a place to render.
  return {
    time: value?.time,
    version: value?.version ?? '2.0.0',
    blocks: [
      {
        type: 'paragraph',
        data: { text: '' },
      },
    ],
  }
}

function waitNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

export function EditorJsField({
  value,
  onChange,
  readOnly,
  label,
  rows = 7,
  hideLabel,
  containerSx,
  holderSx,
  initDelayMs = 0,
  readOnlySurface = 'disabled',
  onFocus,
  onBlur,
  imageUploader,
}: EditorJsFieldProps) {
  const holderRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<import('@editorjs/editorjs').default | null>(null)
  const changeTimeoutRef = useRef<number | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const imageUploaderRef = useRef(imageUploader)
  imageUploaderRef.current = imageUploader

  const [initError, setInitError] = useState<string | null>(null)

  const normalizedValue = useMemo(() => normalizeEditorData(value), [value])
  const valueKey = useMemo(() => JSON.stringify(normalizedValue), [normalizedValue])
  const lastValueKeyRef = useRef<string>(valueKey)

  const effectiveReadOnly = Boolean(readOnly)
  const computedLabel = label ?? 'Editor.js'
  const shouldShowLabel = !hideLabel && Boolean(computedLabel)

  const saveAndEmit = async () => {
    const editor = editorRef.current
    if (!editor) return
    const saved = await editor.save()
    const nextData = saved as EditorJsData
    const normalizedSaved = normalizeEditorData(nextData)
    lastValueKeyRef.current = JSON.stringify(normalizedSaved)
    onChangeRef.current(normalizedSaved)
  }

  useLayoutEffect(() => {
    let destroyed = false
    let delayTimer: number | null = null
    /** Снимок DOM-узла для cleanup (не полагаться на holderRef.current в teardown). */
    let holderForCleanup: HTMLDivElement | null = null

    const run = async () => {
      setInitError(null)
      let holderElement = holderRef.current
      if (!holderElement) {
        await waitNextPaint()
        holderElement = holderRef.current
      }
      if (!holderElement) {
        setInitError('Editor container not ready')
        return
      }

      holderForCleanup = holderElement

      if (initDelayMs > 0) {
        await new Promise<void>((r) => {
          delayTimer = window.setTimeout(() => r(), initDelayMs)
        })
      }
      if (destroyed) return
      await waitNextPaint()
      if (destroyed || !holderRef.current) return

      holderElement.innerHTML = ''

      try {
        const [editorMod, headerMod, paragraphMod, listMod] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/header'),
          import('@editorjs/paragraph'),
          import('@editorjs/list'),
        ])
        const EditorJS = (editorMod.default ?? editorMod) as typeof import('@editorjs/editorjs').default
        const Header = (headerMod as { default?: unknown }).default ?? headerMod
        const Paragraph = (paragraphMod as { default?: unknown }).default ?? paragraphMod
        const List = (listMod as { default?: unknown }).default ?? listMod

        const tools: Record<string, unknown> = {
          header: Header,
          paragraph: Paragraph,
          list: List,
        }

        // image-блок подключается только с загрузчиком (новости лэндинга)
        if (imageUploaderRef.current) {
          const imageMod = await import('@editorjs/image')
          const ImageTool = (imageMod as { default?: unknown }).default ?? imageMod
          tools.image = {
            class: ImageTool,
            config: {
              uploader: {
                uploadByFile: async (file: File) => {
                  const uploaded = await imageUploaderRef.current!(file)
                  return { success: 1, file: uploaded }
                },
              },
            },
          }
        }

        if (destroyed || !holderRef.current) return

        const editor = new EditorJS({
          holder: holderElement,
          data: normalizedValue,
          readOnly: effectiveReadOnly,
          autofocus: false,
          tools: tools as import('@editorjs/editorjs').EditorConfig['tools'],
          onChange: () => {
            if (effectiveReadOnly) return
            if (changeTimeoutRef.current != null) window.clearTimeout(changeTimeoutRef.current)
            changeTimeoutRef.current = window.setTimeout(() => {
              saveAndEmit().catch(() => {})
            }, 350)
          },
        })
        editorRef.current = editor

        await editor.isReady
        if (destroyed) {
          try {
            editor.destroy()
          } catch {
            /* noop */
          }
          return
        }
        lastValueKeyRef.current = valueKey
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e)
        console.error('[EditorJsField] init failed', e)
        setInitError(msg || 'Editor.js init failed')
      }
    }

    void run()

    return () => {
      destroyed = true
      if (delayTimer != null) window.clearTimeout(delayTimer)
      if (changeTimeoutRef.current != null) window.clearTimeout(changeTimeoutRef.current)
      const ed = editorRef.current
      editorRef.current = null
      const holderElement = holderForCleanup
      const cleanup = async () => {
        if (!ed) {
          if (holderElement) holderElement.innerHTML = ''
          return
        }
        try {
          await ed.isReady
        } catch {
          /* noop */
        }
        try {
          ed.destroy()
        } catch {
          /* noop */
        } finally {
          if (holderElement) holderElement.innerHTML = ''
        }
      }
      void cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDelayMs])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    // Update readOnly mode on the fly.
    const ro = effectiveReadOnly
    const reader = editor as unknown as { readOnly?: { toggle?: (value: boolean) => void } }
    reader.readOnly?.toggle?.(ro)
  }, [effectiveReadOnly])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    if (lastValueKeyRef.current === valueKey) return
    lastValueKeyRef.current = valueKey

    const run = async () => {
      try {
        const editorAny = editor as unknown as { clear?: () => Promise<void> | void }
        // Editor.js sometimes can end up appending when re-rendering; clear if supported.
        await editorAny.clear?.()
      } catch {
        // ignore clear errors, render will try to proceed
      }

      editor.render(normalizedValue).catch(() => {
        // If render fails (invalid data), fallback to current state without breaking the UI.
      })
    }

    void run()
  }, [editorRef, valueKey, normalizedValue])

  return (
    <Box
      sx={{ width: '100%', ...(containerSx ?? {}) }}
      onFocusCapture={() => onFocus?.()}
      onBlurCapture={(e) => {
        const next = e.relatedTarget as Node | null
        if (next && e.currentTarget.contains(next)) return
        onBlur?.()
      }}
    >
      {initError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {initError}
        </Alert>
      )}
      {shouldShowLabel && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {computedLabel}
        </Typography>
      )}
      <Box
        ref={holderRef}
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: rows * 28,
          padding: 1,
          backgroundColor: effectiveReadOnly
            ? readOnlySurface === 'paper'
              ? 'background.paper'
              : 'action.disabledBackground'
            : 'background.paper',
          ...(holderSx ?? {}),
        }}
      />
    </Box>
  )
}

